/**
 * @fileoverview Canonical Zod schema for a "Chat with Your Story" session.
 * A session groups an unlimited sequence of user/assistant messages that
 * share a common conversation history within a project.
 */

import { z } from 'zod';
import { ChatMessageSchema } from './message';

/**
 * Lightweight contextual hint passed when a session is opened, letting the
 * assistant ground its opening turn in where the user currently is in the app
 * (e.g., a chapter page) without polluting the message history.
 */
export const ChatContextHintSchema = z.object({
  chapterId: z.string().optional().describe('Chapter the user is currently viewing, if any.'),
  characterId: z.string().optional().describe('Character the user is currently viewing, if any.'),
  worldElementId: z.string().optional().describe('World element the user is currently viewing, if any.'),
});
export type ChatContextHint = z.infer<typeof ChatContextHintSchema>;

/**
 * A single chat session. Users can create unlimited sessions per project
 * and return to any of them from a session picker.
 */
export const ChatSessionSchema = z.object({
  id: z.string().describe('Unique identifier for the session.'),
  projectId: z.string().describe('Project this session belongs to.'),
  title: z.string().describe('Human-readable title (auto-generated from first user turn, editable).'),
  messages: z.array(ChatMessageSchema).default([]).describe('Ordered list of messages in the conversation.'),
  contextHint: ChatContextHintSchema.optional().describe('Optional hint about where the user was when the session started.'),
  createdAt: z.string().describe('ISO timestamp when the session was created.'),
  updatedAt: z.string().describe('ISO timestamp when the session was last updated.'),
});
export type ChatSession = z.infer<typeof ChatSessionSchema>;
